import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Breadcrumbs, { breadcrumbsClasses } from '@mui/material/Breadcrumbs';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import { getMaps } from '../services/api';

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  [`& .${breadcrumbsClasses.separator}`]: {
    color: (theme.vars || theme).palette.action.disabled,
    margin: 1,
  },
  [`& .${breadcrumbsClasses.ol}`]: {
    alignItems: 'center',
  },
}));

export default function NavbarBreadcrumbs() {
  const location = useLocation();
  const navigate = useNavigate();

  // Generate breadcrumbs based on current path
  const generateBreadcrumbs = React.useCallback(async () => {
    const path = location.pathname;
    const segments = path.split('/').filter(segment => segment);

    const breadcrumbs = [];

    // Always start with Dashboard
    breadcrumbs.push({
      label: 'Dashboard',
      path: '/dashboard',
      clickable: true, // Always clickable for uniformity
    });

    if (segments.length > 0) {
      const firstSegment = segments[0];

      switch (firstSegment) {
        case 'dashboard':
          breadcrumbs.push({
            label: 'Home',
            path: '/dashboard',
            clickable: true, // Make clickable for consistency
          });
          break;

        case 'maps':
          if (segments.length === 1) {
            // /maps route
            breadcrumbs.push({
              label: 'Maps',
              path: '/maps',
              clickable: true, // Make clickable for consistency
            });
          } else if (segments.length === 2) {
            // /maps/:id route
            breadcrumbs.push({
              label: 'Maps',
              path: '/maps',
              clickable: true,
            });

            // Try to get map name
            const mapId = segments[1];
            try {
              const mapsData = await getMaps();
              const currentMap = mapsData?.results?.find(
                map => map.id.toString() === mapId
              );
              breadcrumbs.push({
                label: currentMap ? currentMap.name : `Map ${mapId}`,
                path: `/maps/${mapId}`,
                clickable: true, // Make clickable for consistency
              });
            } catch (error) {
              breadcrumbs.push({
                label: `Map ${mapId}`,
                path: `/maps/${mapId}`,
                clickable: true, // Make clickable for consistency
              });
            }
          }
          break;

        case 'organizations':
          breadcrumbs.push({
            label: 'Organizations',
            path: '/organizations',
            clickable: true, // Make clickable for consistency
          });
          break;

        case 'media':
          breadcrumbs.push({
            label: 'Media',
            path: '/media',
            clickable: true,
          });
          break;

        case 'monitor-groups':
          breadcrumbs.push({
            label: 'Monitor Groups',
            path: '/monitor-groups',
            clickable: true,
          });
          break;

        case 'entity-models':
          breadcrumbs.push({
            label: 'Entity Models',
            path: '/entity-models',
            clickable: true,
          });
          break;

        case 'profile':
          breadcrumbs.push({
            label: 'Profile',
            path: '/profile',
            clickable: true,
          });
          break;

        default:
          // Unknown route
          breadcrumbs.push({
            label: firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1),
            path: `/${firstSegment}`,
            clickable: false,
          });
      }
    }

    return breadcrumbs;
  }, [location.pathname]);

  const [breadcrumbs, setBreadcrumbs] = React.useState([]);

  React.useEffect(() => {
    generateBreadcrumbs().then(setBreadcrumbs);
  }, [generateBreadcrumbs]);

  const handleClick = path => {
    navigate(path);
  };

  return (
    <StyledBreadcrumbs
      aria-label="breadcrumb"
      separator={<NavigateNextRoundedIcon fontSize="small" />}
    >
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;

        if (!isLast) {
          // Make all breadcrumbs clickable except the last one
          return (
            <Link
              key={index}
              component="button"
              variant="body1"
              onClick={() => handleClick(crumb.path)}
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'text.secondary',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'text.primary',
                },
                border: 'none',
                background: 'none',
                padding: 0,
                font: 'inherit',
              }}
            >
              {crumb.label}
            </Link>
          );
        }

        return (
          <Typography
            key={index}
            variant="body1"
            sx={{
              color: isLast ? 'text.primary' : 'text.secondary',
              fontWeight: isLast ? 600 : 400,
            }}
          >
            {crumb.label}
          </Typography>
        );
      })}
    </StyledBreadcrumbs>
  );
}
